import React, { useState, useEffect } from 'react';
import { Routes as RouterRoutes, Route, Navigate } from "react-router-dom";
import { Routes } from "../routes";

// pages
import Signin from "./pages/Signin";
import LoginWithOtp from "./pages/LoginWithOtp";
import ReferralValidate from "./pages/ReferralValidate";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import ChangePassword from "./pages/ChangePassword";
import NotFoundPage from "./pages/NotFound";
import ServerError from "./pages/ServerError";

// components
import Sidebar from "../components/Sidebar";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Preloader from "../components/Preloader";

import AdminDashboard from './pages/admin-side/AdminDashboard';
import AdminProfilePage from './pages/admin-side/AdminProfilePage';
import AdminCourseList from './pages/admin-side/AdminCourseList';
import AdminSendNotifications from './pages/admin-side/AdminSendNotifications';
import AdminCommissionList from './pages/admin-side/AdminCommissionList';
import AdminLevelCommissions from './pages/admin-side/AdminLevelCommissions';

import UserDashboard from './pages/user-side/UserDashboard';
import UserProfilePage from './pages/user-side/UserProfilePage';
import UserMyReferrals from './pages/user-side/UserMyReferrals';
import UserLevelEarning from './pages/user-side/UserLevelEarning';
import UserPurchaseCourseList from './pages/user-side/UserPurchaseCourseList';
import UserLevelNetworkPage from './pages/user-side/UserLevelNetworkPage';
import UserWalletPage from './pages/user-side/UserWalletPage';

import { ToastContainer } from 'react-toastify';
import AdminUserManagement from './pages/admin-side/AdminUserManagement';
import AdminAddUser from './pages/admin-side/AdminAddUser';
import AdminEditUser from './pages/admin-side/AdminEditUser';
import AdminUserDetail from './pages/admin-side/AdminUserDetail';
import UserMyCourseList from './pages/user-side/UserMyCourseList';
import AdminTransactionHistory from './pages/admin-side/AdminTransactionHistory';
import AdminSettlementHistory from './pages/admin-side/AdminSettlementHistory';
import UserMyPayments from './pages/user-side/UserMyPayments';
import UserInvoices from './pages/user-side/UserInvoices';
import UserCreditNotes from './pages/user-side/UserCreditNotes';
import UserGst from './pages/user-side/UserGst';
import UserTds from './pages/user-side/UserTds';
import AdminReports from './pages/admin-side/AdminReports';
import AdminPaymentConfirmList from './pages/admin-side/AdminPaymentConfirmList';
import UserWithdrawalHistory from './pages/user-side/UserWithdrawalHistory';
import AdminWithdrawalRequests from './pages/admin-side/AdminWithdrawalRequests';
import UserReports from './pages/user-side/UserReports';
import AdminBonusHistory from './pages/admin-side/AdminBonusHistory';
import AdminBonusSlabSetup from './pages/admin-side/AdminBonusSlabSetup';
import AdminBonusDashboard from './pages/admin-side/AdminBonusDashboard';
import AdminEligibleUsers from './pages/admin-side/AdminEligibleUsers';
import UserMyBonus from './pages/user-side/UserMyBonus';
import AdminEarningStatement from './pages/admin-side/AdminEarningStatement';
import AdminSupportTicketList from './pages/admin-side/AdminSupportTicketList';
import UserSupportTicketList from './pages/user-side/UserSupportTicketList';
import AdminRefundManagement from './pages/admin-side/AdminRefundManagement';
import UserRefundManagement from './pages/user-side/UserRefundManagement';
import AdminUserTransactionHistory from './pages/admin-side/AdminUserTransactionHistory';
import AdminUserCourseList from './pages/admin-side/AdminUserCourseList';
import UserMyIds from './pages/user-side/MyIds/index';
import UserMyTeam from './pages/user-side/UserMyTeam';
import UserCollaborativePurchase from './pages/user-side/UserCollaborativePurchase';
import SearchResults from './pages/SearchResults';
import AdminLogs from './pages/admin-side/AdminLogs'
import UserSettlementHistory from './pages/user-side/UserSettlementHistory';

const getStoredAuthUser = () => {
  try {
    const raw = sessionStorage.getItem("authUser");
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
};

const getStoredRole = () => {
  const user = getStoredAuthUser();
  return String(user?.user_type || sessionStorage.getItem("authRole") || "").trim().toUpperCase();
};

const getDashboardPathByRole = (role) => {
  const normalizedRole = String(role || "").trim().toUpperCase();

  switch (normalizedRole) {
    case "ADMIN":
    case "SUPER_ADMIN":
    case "ROLE_ADMIN":
    case "ROLE_SUPER_ADMIN":
      return Routes.AdminDashboard.path;

    case "USER":
    case "ROLE_USER":
    case "CHILD":
    case "ROLE_CHILD":
      return Routes.UserDashboard.path;
    default:
      return Routes.UserDashboard.path;
  }
};

const isAuthenticated = () => {
  return !!sessionStorage.getItem("authToken");
};

const hasAccessToRole = (allowedRoles = []) => {
  if (!allowedRoles || allowedRoles.length === 0) return true;

  const currentRole = getStoredRole();
  return allowedRoles.map((role) => String(role).trim().toUpperCase()).includes(currentRole);
};

const HomeRedirect = () => {
  if (!isAuthenticated()) {
    return <Navigate to={Routes.Signin.path} replace />;
  }

  return <Navigate to={getDashboardPathByRole(getStoredRole())} replace />;
};

const RouteWithLoader = ({ Component }) => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      {/* <Preloader show={loaded ? false : true} /> */}
      <Component />
    </>
  );
};

const RouteWithSidebar = ({ Component, allowedRoles = [] }) => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  const localStorageIsSettingsVisible = () => {
    return localStorage.getItem('settingsVisible') === 'false' ? false : true;
  };

  const [showSettings, setShowSettings] = useState(localStorageIsSettingsVisible);

  const toggleSettings = () => {
    setShowSettings(!showSettings);
    localStorage.setItem('settingsVisible', !showSettings);
  };

  if (!isAuthenticated()) {
    return <Navigate to={Routes.Signin.path} replace />;
  }

  if (!hasAccessToRole(allowedRoles)) {
    return <Navigate to={getDashboardPathByRole(getStoredRole())} replace />;
  }

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      {/* <Preloader show={loaded ? false : true} /> */}
      <Sidebar />

      <main className="content" style={{ overflowX: "hidden", minWidth: 0 }}>
        <Navbar />
        <Component />
        <Footer toggleSettings={toggleSettings} showSettings={showSettings} />
      </main>
    </>
  );
};

export default () => (
  <RouterRoutes>
    <Route path="/" element={<HomeRedirect />} />

    <Route path={Routes.Signin.path} element={<RouteWithLoader Component={Signin} />} />
    <Route path={Routes.ReferralValidate.path} element={<RouteWithLoader Component={ReferralValidate} />} />
    <Route path={Routes.Register.path} element={<RouteWithLoader Component={Register} />} />
    <Route path={Routes.LoginWithOtp.path} element={<RouteWithLoader Component={LoginWithOtp} />} />
    <Route path={Routes.ChangePassword.path} element={<RouteWithLoader Component={ChangePassword} />} />
    <Route path={Routes.ForgotPassword.path} element={<RouteWithLoader Component={ForgotPassword} />} />
    <Route path={Routes.NotFound.path} element={<RouteWithLoader Component={NotFoundPage} />} />
    <Route path={Routes.ServerError.path} element={<RouteWithLoader Component={ServerError} />} />

    {/* pages */}
    <Route
      path={Routes.AdminDashboard.path}
      element={
        <RouteWithSidebar
          Component={AdminDashboard}
          allowedRoles={["ADMIN", "SUPER_ADMIN", "ROLE_ADMIN", "ROLE_SUPER_ADMIN"]}
        />
      }
    />
    <Route
      path={Routes.AdminProfilePage.path}
      element={
        <RouteWithSidebar
          Component={AdminProfilePage}
          allowedRoles={["ADMIN", "SUPER_ADMIN", "ROLE_ADMIN", "ROLE_SUPER_ADMIN"]}
        />
      }
    />
    <Route
      path={Routes.AdminCourseList.path}
      element={
        <RouteWithSidebar
          Component={AdminCourseList}
          allowedRoles={["ADMIN", "SUPER_ADMIN", "ROLE_ADMIN", "ROLE_SUPER_ADMIN"]}
        />
      }
    />
    <Route
      path={Routes.AdminSendNotifications.path}
      element={
        <RouteWithSidebar
          Component={AdminSendNotifications}
          allowedRoles={["ADMIN", "SUPER_ADMIN", "ROLE_ADMIN", "ROLE_SUPER_ADMIN"]}
        />
      }
    />
    <Route
      path={Routes.AdminCommissionList.path}
      element={
        <RouteWithSidebar
          Component={AdminCommissionList}
          allowedRoles={["ADMIN", "SUPER_ADMIN", "ROLE_ADMIN", "ROLE_SUPER_ADMIN"]}
        />
      }
    />
    <Route
      path={Routes.AdminLevelCommissions.path}
      element={
        <RouteWithSidebar
          Component={AdminLevelCommissions}
          allowedRoles={["ADMIN", "SUPER_ADMIN", "ROLE_ADMIN", "ROLE_SUPER_ADMIN"]}
        />
      }
    />
    <Route
      path={Routes.AdminUserManagement.path}
      element={
        <RouteWithSidebar
          Component={AdminUserManagement}
          allowedRoles={["ADMIN", "SUPER_ADMIN", "ROLE_ADMIN", "ROLE_SUPER_ADMIN"]}
        />
      }
    />
    <Route
      path={Routes.AdminAddUser.path}
      element={
        <RouteWithSidebar
          Component={AdminAddUser}
          allowedRoles={["ADMIN", "SUPER_ADMIN", "ROLE_ADMIN", "ROLE_SUPER_ADMIN"]}
        />
      }
    />
    <Route
      path={Routes.AdminEditUser.path}
      element={
        <RouteWithSidebar
          Component={AdminEditUser}
          allowedRoles={["ADMIN", "SUPER_ADMIN", "ROLE_ADMIN", "ROLE_SUPER_ADMIN"]}
        />
      }
    />
    <Route
      path={Routes.AdminUserDetails.path}
      element={
        <RouteWithSidebar
          Component={AdminUserDetail}
          allowedRoles={["ADMIN", "SUPER_ADMIN", "ROLE_ADMIN", "ROLE_SUPER_ADMIN"]}
        />
      }
    />
    <Route
      path={Routes.AdminTransactionHistory.path}
      element={
        <RouteWithSidebar
          Component={AdminTransactionHistory}
          allowedRoles={["ADMIN", "SUPER_ADMIN", "ROLE_ADMIN", "ROLE_SUPER_ADMIN"]}
        />
      }
    />
    <Route
      path={Routes.AdminSettlementHistory.path}
      element={
        <RouteWithSidebar
          Component={AdminSettlementHistory}
          allowedRoles={["ADMIN", "SUPER_ADMIN", "ROLE_ADMIN", "ROLE_SUPER_ADMIN"]}
        />
      }
    />
    <Route
      path={Routes.AdminReports.path}
      element={
        <RouteWithSidebar
          Component={AdminReports}
          allowedRoles={["ADMIN", "SUPER_ADMIN", "ROLE_ADMIN", "ROLE_SUPER_ADMIN"]}
        />
      }
    />
    <Route
      path={Routes.AdminPaymentConfirmList.path}
      element={
        <RouteWithSidebar
          Component={AdminPaymentConfirmList}
          allowedRoles={["ADMIN", "SUPER_ADMIN", "ROLE_ADMIN", "ROLE_SUPER_ADMIN"]}
        />
      }
    />
    <Route
      path={Routes.AdminWithdrawalRequests.path}
      element={
        <RouteWithSidebar
          Component={AdminWithdrawalRequests}
          allowedRoles={["ADMIN", "SUPER_ADMIN", "ROLE_ADMIN", "ROLE_SUPER_ADMIN"]}
        />
      }
    />
    <Route
      path={Routes.AdminEarningStatement.path}
      element={
        <RouteWithSidebar
          Component={AdminEarningStatement}
          allowedRoles={["ADMIN", "SUPER_ADMIN", "ROLE_ADMIN", "ROLE_SUPER_ADMIN"]}
        />
      }
    />
    <Route
      path={Routes.AdminBonusDashboard.path}
      element={
        <RouteWithSidebar
          Component={AdminBonusDashboard}
          allowedRoles={["ADMIN", "SUPER_ADMIN", "ROLE_ADMIN", "ROLE_SUPER_ADMIN"]}
        />
      }
    />
    <Route
      path={Routes.AdminBonusSlabSetup.path}
      element={
        <RouteWithSidebar
          Component={AdminBonusSlabSetup}
          allowedRoles={["ADMIN", "SUPER_ADMIN", "ROLE_ADMIN", "ROLE_SUPER_ADMIN"]}
        />
      }
    />
    <Route
      path={Routes.AdminEligibleUsers.path}
      element={
        <RouteWithSidebar
          Component={AdminEligibleUsers}
          allowedRoles={["ADMIN", "SUPER_ADMIN", "ROLE_ADMIN", "ROLE_SUPER_ADMIN"]}
        />
      }
    />
    <Route
      path={Routes.AdminBonusHistory.path}
      element={
        <RouteWithSidebar
          Component={AdminBonusHistory}
          allowedRoles={["ADMIN", "SUPER_ADMIN", "ROLE_ADMIN", "ROLE_SUPER_ADMIN"]}
        />
      }
    />
    <Route
      path={Routes.AdminSupportTicketList.path}
      element={
        <RouteWithSidebar
          Component={AdminSupportTicketList}
          allowedRoles={["ADMIN", "SUPER_ADMIN", "ROLE_ADMIN", "ROLE_SUPER_ADMIN"]}
        />
      }
    />
    <Route
      path={Routes.AdminRefundManagement.path}
      element={
        <RouteWithSidebar
          Component={AdminRefundManagement}
          allowedRoles={["ADMIN", "SUPER_ADMIN", "ROLE_ADMIN", "ROLE_SUPER_ADMIN"]}
        />
      }
    />
    <Route
      path={Routes.AdminUserTransactionHistory.path}
      element={
        <RouteWithSidebar
          Component={AdminUserTransactionHistory}
          allowedRoles={["ADMIN", "SUPER_ADMIN", "ROLE_ADMIN", "ROLE_SUPER_ADMIN"]}
        />
      }
    />
    <Route
      path={Routes.AdminUserCourseList.path}
      element={
        <RouteWithSidebar
          Component={AdminUserCourseList}
          allowedRoles={["ADMIN", "SUPER_ADMIN", "ROLE_ADMIN", "ROLE_SUPER_ADMIN"]}
        />
      }
    />
    <Route
      path={Routes.AdminLogs.path}
      element={
        <RouteWithSidebar
          Component={AdminLogs}
          allowedRoles={["ADMIN", "SUPER_ADMIN", "ROLE_ADMIN", "ROLE_SUPER_ADMIN"]}
        />
      }
    />






    <Route
      path={Routes.UserDashboard.path}
      element={
        <RouteWithSidebar
          Component={UserDashboard}
          allowedRoles={["USER", "ROLE_USER", "CHILD", "ROLE_CHILD"]}
        />
      }
    />
    <Route
      path={Routes.UserProfilePage.path}
      element={
        <RouteWithSidebar
          Component={UserProfilePage}
          allowedRoles={["USER", "ROLE_USER", "CHILD", "ROLE_CHILD"]}
        />
      }
    />
    <Route
      path={Routes.UserMyCourseList.path}
      element={
        <RouteWithSidebar
          Component={UserMyCourseList}
          allowedRoles={["USER", "ROLE_USER", "CHILD", "ROLE_CHILD"]}
        />
      }
    />
    <Route
      path={Routes.UserMyReferrals.path}
      element={
        <RouteWithSidebar
          Component={UserMyReferrals}
          allowedRoles={["USER", "ROLE_USER", "CHILD", "ROLE_CHILD"]}
        />
      }
    />
    <Route
      path={Routes.UserLevelEarning.path}
      element={
        <RouteWithSidebar
          Component={UserLevelEarning}
          allowedRoles={["USER", "ROLE_USER", "CHILD", "ROLE_CHILD"]}
        />
      }
    />
    <Route
      path={Routes.UserPurchaseCourseList.path}
      element={
        <RouteWithSidebar
          Component={UserPurchaseCourseList}
          allowedRoles={["USER", "ROLE_USER", "CHILD", "ROLE_CHILD"]}
        />
      }
    />

    <Route
      path={Routes.UserLevelNetworkPage.path}
      element={
        <RouteWithSidebar
          Component={UserLevelNetworkPage}
          allowedRoles={["USER", "ROLE_USER", "CHILD", "ROLE_CHILD"]}
        />
      }
    />
    <Route
      path={Routes.UserCollaborativePurchase.path}
      element={
        <RouteWithSidebar
          Component={UserCollaborativePurchase}
          allowedRoles={["USER", "ROLE_USER", "CHILD", "ROLE_CHILD"]}
        />
      }
    />
    <Route
      path={Routes.UserMyTeam.path}
      element={
        <RouteWithSidebar
          Component={UserMyTeam}
          allowedRoles={["USER", "ROLE_USER", "CHILD", "ROLE_CHILD"]}
        />
      }
    />
    <Route
      path={Routes.UserMyIds.path}
      element={
        <RouteWithSidebar
          Component={UserMyIds}
          allowedRoles={["USER", "ROLE_USER", "CHILD", "ROLE_CHILD"]}
        />
      }
    />
    <Route
      path={Routes.UserWalletPage.path}
      element={
        <RouteWithSidebar
          Component={UserWalletPage}
          allowedRoles={["USER", "ROLE_USER", "CHILD", "ROLE_CHILD"]}
        />
      }
    />
    <Route
      path={Routes.UserMyPayments.path}
      element={
        <RouteWithSidebar
          Component={UserMyPayments}
          allowedRoles={["USER", "ROLE_USER", "CHILD", "ROLE_CHILD"]}
        />
      }
    />
    <Route
      path={Routes.UserInvoices.path}
      element={
        <RouteWithSidebar
          Component={UserInvoices}
          allowedRoles={["USER", "ROLE_USER", "CHILD", "ROLE_CHILD"]}
        />
      }
    />
    <Route
      path={Routes.UserCreditNotes.path}
      element={
        <RouteWithSidebar
          Component={UserCreditNotes}
          allowedRoles={["USER", "ROLE_USER", "CHILD", "ROLE_CHILD"]}
        />
      }
    />
    <Route
      path={Routes.UserGst.path}
      element={
        <RouteWithSidebar
          Component={UserGst}
          allowedRoles={["USER", "ROLE_USER", "CHILD", "ROLE_CHILD"]}
        />
      }
    />
    <Route
      path={Routes.UserTds.path}
      element={
        <RouteWithSidebar
          Component={UserTds}
          allowedRoles={["USER", "ROLE_USER", "CHILD", "ROLE_CHILD"]}
        />
      }
    />
    <Route
      path={Routes.UserWithdrawalHistory.path}
      element={
        <RouteWithSidebar
          Component={UserWithdrawalHistory}
          allowedRoles={["USER", "ROLE_USER", "CHILD", "ROLE_CHILD"]}
        />
      }
    />
    <Route
      path={Routes.UserReports.path}
      element={
        <RouteWithSidebar
          Component={UserReports}
          allowedRoles={["USER", "ROLE_USER", "CHILD", "ROLE_CHILD"]}
        />
      }
    />
    <Route
      path={Routes.UserMyBonus.path}
      element={
        <RouteWithSidebar
          Component={UserMyBonus}
          allowedRoles={["USER", "ROLE_USER", "CHILD", "ROLE_CHILD"]}
        />
      }
    />
    <Route
      path={Routes.UserRefundManagement.path}
      element={
        <RouteWithSidebar
          Component={UserRefundManagement}
          allowedRoles={["USER", "ROLE_USER", "CHILD", "ROLE_CHILD"]}
        />
      }
    />


    <Route
      path={Routes.UserSupportTicketList.path}
      element={
        <RouteWithSidebar
          Component={UserSupportTicketList}
          allowedRoles={["USER", "ROLE_USER", "CHILD", "ROLE_CHILD"]}
        />
      }
    />

    <Route
      path={Routes.UserSettlementHistory.path}
      element={
        <RouteWithSidebar
          Component={UserSettlementHistory}
          allowedRoles={["USER", "ROLE_USER", "CHILD", "ROLE_CHILD"]}
        />
      }
    />


     <Route
      path={Routes.SearchResults.path}
      element={
        <RouteWithSidebar
          Component={SearchResults}
          allowedRoles={["ADMIN", "SUPER_ADMIN", "ROLE_ADMIN", "ROLE_SUPER_ADMIN", "USER", "ROLE_USER", "CHILD", "ROLE_CHILD"]}
        />
      }
    />



    <Route path="*" element={<Navigate to={Routes.NotFound.path} replace />} />
  </RouterRoutes>
);